import { useEffect, useState } from 'react';

/**
 * Tracks whether the viewport satisfies a CSS media query for responsive rendering. Seeds state from an SSR-safe matchMedia check, re-evaluates on mount and whenever the query changes, and subscribes to change events through addListener or addEventListener, removing the listener on cleanup.
 *
 * @param query - CSS media query to track.
 * @returns True while the viewport matches the query.
 */
export function useMediaQuery(query: string) {
  /**
   * Evaluates a CSS media query against the current window. Returns the matchMedia result, or false when window is undefined so server-side rendering does not break.
   *
   * @param query - CSS media query to evaluate.
   * @returns True when the viewport matches the query.
   */
  const getMatches = (query: string) => {
    // Prevents SSR issues
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  };

  const [matches, setMatches] = useState(getMatches(query));

  /**
   * Refreshes the stored match result after the media query state changed. Re-evaluates the query against matchMedia and writes the boolean into state.
   *
   * @returns Nothing.
   */
  function handleChange() {
    setMatches(getMatches(query));
  }

  useEffect(() => {
    const matchMedia = window.matchMedia(query);

    // Triggered at the first client-side load and if query changes
    handleChange();

    // Listen matchMedia
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener('change', handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener('change', handleChange);
      }
    };
  }, [query]);

  return matches;
}
